<template>
    <div id="visitor-task-list-view">
        <div v-if="showCreateButton" class="create-task-header">
            <SupsisButton primary @click="createTask" classNames="create-task-button">
                <span class="material-icons">add</span>
                {{ $t("task.createTask") }}
            </SupsisButton>
        </div>
        <div v-if="isLoading && tasks.length === 0" class="loading-container">
            <a-spin size="large">
                <span slot="tip" class="loading-text">Görevler yükleniyor...</span>
            </a-spin>
        </div>
        <div v-else-if="tasks.length === 0 && !isLoading" class="empty-state">
            <div class="empty-state-content">
                <span class="material-icons empty-icon">assignment</span>
                <p class="empty-text">
                    {{ visitorId ? 'Bu ziyaretçiye atanmış görev bulunmamaktadır' : 'Bu kayda atanmış görev bulunmamaktadır' }}
                </p>
            </div>
        </div>
        <div v-else class="visitor-task-table">
            <supsis-table
                :columns="columns"
                :rows="tasks"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                :options="tableOptions"
                :loading="isLoading"
                @cursor-changed="handlePaginationChange"
            />
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";
import SupsisTable from "../SupsisTable.vue";
import LowIcon from "../svg/task/LowIcon.vue";
import LowestIcon from "../svg/task/LowestIcon.vue";
import MediumIcon from "../svg/task/MediumIcon.vue";
import HighIcon from "../svg/task/HighIcon.vue";
import HighestIcon from "../svg/task/HighestIcon.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import { deleteTask } from "../../api/tasks";
import { publishMessage, SystemMessageTypes } from "../../utils/systemPubSub";

export default {
    name: "VisitorTaskListView",
    components: {
        SupsisTable,
        Avatar,
        LowIcon,
        LowestIcon,
        MediumIcon,
        HighIcon,
        HighestIcon,
        SupsisButton,
    },
    props: {
        visitorId: {
            type: String,
            default: null,
        },
        tableRecord: {
            type: String,
            default: null,
        },
        showCreateButton: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: false,
            tasks: [],
            paginationCursor: {
                total: 0,
                page: 1,
                perPage: 20,
            },
            lastRequestParams: null,
            lastResponseWasEmpty: false,
            columns: [
                {
                    title: this.$t("task.taskID"),
                    field: "customId",
                    key: "customId",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="task-id-cell">
                                <span class="material-icons">fingerprint</span>
                                <span class="task-id-text">{row.customId}</span>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.task"),
                    field: "title",
                    key: "title",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class={"title-cell"} v-tippy={{ content: row.title }}>
                                {row.title}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.workflow"),
                    field: "workflow",
                    key: "workflow",
                    renderBodyCell: ({ row }) => {
                        const workflow = row?.workflow && this.workflows[row?.workflow?._id];
                        return workflow?.title ? (
                            <div class="workflow-cell">
                                <span class="material-icons">view_kanban</span>
                                <span>{workflow.title}</span>
                            </div>
                        ) : "";
                    },
                },
                {
                    title: this.$t("task.stage"),
                    field: "pipelineStage",
                    key: "pipelineStage",
                    renderBodyCell: ({ row }) => {
                        return row?.pipelineStage ? (
                            <div
                                class="supsis-tag-el"
                                style={{ "--pipeline-color": row?.pipelineStage?.color || "#000" }}
                            >
                                {row?.pipelineStage?.name || ""}
                            </div>
                        ) : null;
                    },
                },
                {
                    title: this.$t("task.owner"),
                    field: "owner",
                    key: "owner",
                    renderBodyCell: ({ row }) => {
                        const user = row.owner ? this.users[row.owner] : null;
                        return user && (
                            <div class="agent-detail">
                                <Avatar
                                    size={25}
                                    key={user._id}
                                    username={user.name}
                                    src={user.gravatar}
                                    v-tippy={{
                                        followCursor: true,
                                        placement: "bottom-end",
                                    }}
                                    content={user.email}
                                />
                                <div v-tippy={{ content: user.name }}>
                                    {user.name.substring(0, 8) + "..."}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("task.assignees"),
                    field: "assignees",
                    key: "assignees",
                    renderBodyCell: ({ row }) => {
                        const assignee = row?.assignees?.[0];
                        const user = assignee ? this.users[assignee] : null;
                        return user && (
                            <div class="agent-detail">
                                <Avatar
                                    size={25}
                                    key={assignee}
                                    src={user?.gravatar}
                                    username={user?.name}
                                    v-tippy={{
                                        followCursor: true,
                                        placement: "bottom-end",
                                    }}
                                    content={user?.email}
                                />
                                <div v-tippy={{ content: user?.name }}>
                                    {(user?.name || "").substring(0, 8) + "..."}
                                </div>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.priority"),
                    field: "priority",
                    key: "priority",
                    renderBodyCell: ({ row }) => {
                        return row.priority === "low" ? (
                            <div v-tippy={{ content: "Low" }}>
                                <LowIcon />
                            </div>
                        ) : row.priority === "lowest" ? (
                            <div v-tippy={{ content: "Lowest" }}>
                                <LowestIcon />
                            </div>
                        ) : row.priority === "medium" ? (
                            <div v-tippy={{ content: "Medium" }}>
                                <MediumIcon />
                            </div>
                        ) : row.priority === "high" ? (
                            <div v-tippy={{ content: "High" }}>
                                <HighIcon />
                            </div>
                        ) : row.priority === "highest" ? (
                            <div v-tippy={{ content: "Highest" }}>
                                <HighestIcon />
                            </div>
                        ) : (
                            ""
                        );
                    },
                },
                {
                    title: this.$t("message.endDate"),
                    field: "endDate",
                    key: "endDate",
                    renderBodyCell: ({ row }) => {
                        if (!row.endDate) return "";
                        const now = new Date();
                        const endDate = new Date(row.endDate);
                        const isOverdue = endDate < now;
                        return (
                            <div class="date-cell" style={{ color: isOverdue ? '#f5576c' : '#6c757d' }}>
                                <span class="material-icons" style={{ color: isOverdue ? '#f5576c' : '#91a2b6' }}>
                                    {isOverdue ? 'warning' : 'flag'}
                                </span>
                                <span>{this.formatDate(row.endDate)}</span>
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("message.transactions"),
                    field: "_id",
                    key: "_id",
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        const refreshTable = this.refreshTable;
                        return (
                            <div class="task-transactions">
                                <button
                                    class="action-btn edit-btn"
                                    onClick={() => {
                                        this.addNewWindow({
                                            type: "editor-task",
                                            title: row.title,
                                            payload: {
                                                taskId: row._id,
                                                onTaskCreated: () => {
                                                    refreshTable();
                                                },
                                            },
                                        });
                                    }}
                                    v-tippy={{ content: this.$t("message.edit") }}
                                >
                                    <span class="material-icons">edit</span>
                                </button>
                                <button
                                    class="action-btn delete-btn"
                                    onClick={() => this.confirmDeleteTask(row)}
                                    v-tippy={{ content: this.$t("message.delete") }}
                                >
                                    <span class="material-icons">delete</span>
                                </button>
                            </div>
                        );
                    },
                },
            ],
            tableOptions: {
                bodyCellEvents: ({ row, column, rowIndx }) => {
                    return {
                        dblclick: (event) => {
                            this.addNewWindow({
                                type: "edit-workflow",
                                title: row.title,
                                payload: {
                                    taskId: row._id,
                                },
                            });
                        },
                    };
                },
            },
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
    },
    mounted() {
        // İlk yükleme
        if (this.visitorId || this.tableRecord) {
            console.log('VisitorTaskListView mounted: İlk yükleme', {
                visitorId: this.visitorId,
                tableRecord: this.tableRecord
            });
            this.loadData();
        }
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            getListTasks: "tasks/getListTasks",
        }),
        loadData() {
            console.log("VisitorTaskListView - Veri yükleniyor...");
            this.getTasks();
        },
        handlePaginationChange(cursor) {
            console.log("VisitorTaskListView - Sayfalama değişti:", cursor);
            this.paginationCursor = {
                ...this.paginationCursor,
                page: cursor.page,
                perPage: cursor.perPage
            };
            this.lastRequestParams = null;
            this.lastResponseWasEmpty = false;
            this.getTasks();
        },
        refreshTable() {
            this.lastRequestParams = null;
            this.lastResponseWasEmpty = false;
            this.paginationCursor = {
                ...this.paginationCursor,
                page: 1
            };
            this.getTasks();
        },
        formatDate(date) {
            return date && this.$moment(date).format("YYYY/MM/DD HH:mm");
        },
        getTasks() {
            // Eğer zaten yükleniyorsa, tekrar çağırma
            if (this.isLoading) {
                console.log('VisitorTaskListView - getTasks: Zaten yükleniyor, atlanıyor...');
                return;
            }

            // visitorId veya tableRecord'dan en az biri olmalı
            if (!this.visitorId && !this.tableRecord) {
                console.log('VisitorTaskListView - getTasks: visitorId ve tableRecord yok, atlanıyor...');
                return;
            }

            // Request parametrelerini oluştur
            const params = {
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
            };

            // visitorId varsa ekle
            if (this.visitorId) {
                params.visitorId = this.visitorId;
            }

            // tableRecord varsa ekle
            if (this.tableRecord) {
                params.tableRecord = this.tableRecord;
            }

            // Aynı parametrelerle tekrar istek atılmasını engelle
            if (this.lastRequestParams) {
                const paramsString = JSON.stringify(params);
                const lastParamsString = JSON.stringify(this.lastRequestParams);
                if (paramsString === lastParamsString) {
                    // Eğer son istek boş data döndüyse, tekrar istek atma
                    if (this.lastResponseWasEmpty) {
                        console.log('VisitorTaskListView - getTasks: Son istek boş data döndü ve parametreler değişmedi, atlanıyor...');
                        return;
                    }
                    console.log('VisitorTaskListView - getTasks: Aynı parametrelerle tekrar istek atılmaya çalışılıyor, atlanıyor...');
                    return;
                }
            }

            // Son request parametrelerini kaydet
            this.lastRequestParams = JSON.parse(JSON.stringify(params));
            this.lastResponseWasEmpty = false;

            this.isLoading = true;

            console.log('VisitorTaskListView - Filtrelerle görevler yükleniyor:', params);

            this.getListTasks(params).then(({ data, total }) => {
                const responseData = data || [];
                const responseTotal = total || 0;
                
                // Boş data kontrolü
                this.lastResponseWasEmpty = responseData.length === 0 && responseTotal === 0;
                
                this.tasks = responseData;
                this.paginationCursor = {
                    ...this.paginationCursor,
                    total: responseTotal
                };
                
                if (this.lastResponseWasEmpty) {
                    console.log('VisitorTaskListView - getTasks: Boş data alındı, tekrar istek atılmayacak (parametreler değişmediği sürece)');
                }
            }).catch(error => {
                console.error('VisitorTaskListView - Görevler yüklenirken hata oluştu:', error);
                this.tasks = [];
                this.paginationCursor = {
                    ...this.paginationCursor,
                    total: 0
                };
                // Hata durumunda da boş data olarak işaretle
                this.lastResponseWasEmpty = true;
                this.$notification.error({
                    message: 'Hata',
                    description: 'Görevler yüklenirken bir hata oluştu.'
                });
            }).finally(() => {
                this.isLoading = false;
            });
        },
        confirmDeleteTask(task) {
            const workflow = task.workflow;
            if (workflow && workflow.permissions) {
                const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                    (permission) => workflow.permissions[permission] || [],
                );
                const perm = permissions.every((permission) => permission.length === 0);
                if (perm || permissions[3].includes(this.user._id)) {
                    this.$confirm({
                        title: "Silme İşlemi",
                        content: "Bu görevi silmek istediğinize emin misiniz?",
                        okText: this.$t("message.yes"),
                        cancelText: this.$t("message.no"),
                        onOk: () => {
                            this.deleteTask(task._id);
                        },
                    });
                } else {
                    this.$notification.error({
                        message: this.$t("task.deleteTaskError"),
                        description: this.$t("task.deleteTaskErrorDesc"),
                    });
                }
            } else {
                console.error("Workflow permissions are undefined");
            }
        },
        deleteTask(taskId) {
            deleteTask(taskId)
                .then(() => {
                    publishMessage(SystemMessageTypes.TASK_DELETED, {
                        taskId,
                    });
                    this.refreshTable();
                    this.$message.success(`Görev başarıyla silindi`);
                })
                .catch((error) => {
                    console.error("Görev silinirken hata oluştu:", error);
                    this.$message.error(`Görev silinirken hata oluştu`);
                });
        },
        createTask() {
            const refreshTable = this.refreshTable;
            const payload = {
                onTaskCreated: () => {
                    refreshTable();
                },
            };

            // visitorId varsa ekle
            if (this.visitorId) {
                payload.visitorId = this.visitorId;
            }

            // tableRecord varsa ekle
            if (this.tableRecord) {
                payload.tableRecord = this.tableRecord;
            }

            this.addNewWindow({
                type: "chat-create-task",
                title: this.$t("task.createTask"),
                payload: payload,
            });
        },
    },
    watch: {
        visitorId: {
            handler(newVal, oldVal) {
                // visitorId değiştiğinde yeniden yükle
                // oldVal undefined/null ise, ilk kez set ediliyor demektir
                // mounted zaten loadData() çağıracak, burada tekrar yüklemeye gerek yok
                if (oldVal === undefined || oldVal === null) {
                    console.log('VisitorTaskListView - visitorId ilk kez set ediliyor, mounted yükleyecek:', newVal);
                    return;
                }
                
                // Aynı değer ise, tekrar istek atma
                if (newVal === oldVal) {
                    console.log('VisitorTaskListView - visitorId aynı değer, atlanıyor:', newVal);
                    return;
                }
                
                // Gerçek bir değişiklik var ve yüklenmiyorsa
                if (newVal !== oldVal && !this.isLoading) {
                    console.log('VisitorTaskListView - visitorId değişti, yeniden yükleniyor:', { oldVal, newVal });
                    this.lastRequestParams = null;
                    this.lastResponseWasEmpty = false;
                    this.paginationCursor = {
                        ...this.paginationCursor,
                        page: 1
                    };
                    this.getTasks();
                }
            },
            immediate: false
        },
        tableRecord: {
            handler(newVal, oldVal) {
                // tableRecord değiştiğinde yeniden yükle
                // oldVal undefined/null ise, ilk kez set ediliyor demektir
                // mounted zaten loadData() çağıracak, burada tekrar yüklemeye gerek yok
                if (oldVal === undefined || oldVal === null) {
                    console.log('VisitorTaskListView - tableRecord ilk kez set ediliyor, mounted yükleyecek:', newVal);
                    return;
                }
                
                // Aynı değer ise, tekrar istek atma
                if (newVal === oldVal) {
                    console.log('VisitorTaskListView - tableRecord aynı değer, atlanıyor:', newVal);
                    return;
                }
                
                // Gerçek bir değişiklik var ve yüklenmiyorsa
                if (newVal !== oldVal && !this.isLoading) {
                    console.log('VisitorTaskListView - tableRecord değişti, yeniden yükleniyor:', { oldVal, newVal });
                    this.lastRequestParams = null;
                    this.lastResponseWasEmpty = false;
                    this.paginationCursor = {
                        ...this.paginationCursor,
                        page: 1
                    };
                    this.getTasks();
                }
            },
            immediate: false
        }
    }
};
</script>
<style lang="scss">
#visitor-task-list-view {
    background: transparent;
    min-height: 100%;
    padding: 0;

    .create-task-header {
        display: flex;
        gap: 10px;
        margin-bottom: 16px;
        padding: 0 4px;

        .create-task-button {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 10px 20px;
            font-size: 14px;
            font-weight: 600;
            border-radius: 6px;
            transition: all 0.2s ease;

            .material-icons {
                font-size: 18px;
            }

            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        background: white;
        border-radius: 8px;
        border: 1px solid #e8ebed;

        .loading-text {
            color: #6c757d;
            font-size: 14px;
            margin-top: 12px;
        }
    }

    .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        padding: 40px 20px;

        .empty-state-content {
            text-align: center;

            .empty-icon {
                font-size: 64px;
                color: #cbd5e1;
                margin-bottom: 16px;
                display: block;
            }

            .empty-text {
                color: #6b7280;
                font-size: 14px;
                margin: 0;
            }
        }
    }

    .task-transactions {
        display: flex;
        gap: 8px;
        align-items: center;
        justify-content: center;

        .action-btn {
            width: 36px;
            height: 36px;
            min-width: 36px;
            min-height: 36px;
            max-width: 36px;
            max-height: 36px;
            border-radius: 8px;
            border: 1.5px solid;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            padding: 0;
            margin: 0;
            box-sizing: border-box;
            position: relative;
            flex-shrink: 0;

            .material-icons {
                font-size: 18px;
                line-height: 1;
                transition: all 0.2s ease;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            &:hover {
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }

            &:active {
                transform: translateY(0);
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
            }

            &.edit-btn {
                background-color: rgba(99, 102, 241, 0.1);
                border-color: rgba(99, 102, 241, 0.3);

                .material-icons {
                    color: #6366f1;
                }

                &:hover {
                    background-color: rgba(99, 102, 241, 0.15);
                    border-color: rgba(99, 102, 241, 0.4);
                    
                    .material-icons {
                        color: #4f46e5;
                    }
                }
            }

            &.delete-btn {
                background-color: rgba(239, 68, 68, 0.1);
                border-color: rgba(239, 68, 68, 0.3);

                .material-icons {
                    color: #ef4444;
                }

                &:hover {
                    background-color: rgba(239, 68, 68, 0.15);
                    border-color: rgba(239, 68, 68, 0.4);
                    
                    .material-icons {
                        color: #dc2626;
                    }
                }
            }
        }
    }

    .title-cell {
        max-width: 300px;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
        color: #2c3e50;
        font-size: 14px;
    }

    .visitor-task-table {
        padding: 0;
        background: white;
        border-radius: 8px;
        border: 1px solid #e8ebed;
        overflow: hidden;

        :deep(.supsis-table) {
            border-radius: 8px;
            
            .table-header {
                background: #f8f9fa;
                border-bottom: 2px solid #e8ebed;
                
                th {
                    color: #495057 !important;
                    font-weight: 600;
                    font-size: 13px;
                    padding: 14px 12px !important;
                    border-bottom: none !important;
                }
            }

            .table-body {
                tr {
                    border-bottom: 1px solid #f0f2f5;

                    &:hover {
                        background: #f8f9fa !important;
                    }

                    &:last-child {
                        border-bottom: none;
                    }

                    td {
                        padding: 12px !important;
                        vertical-align: middle;
                        color: #55555a;
                        font-size: 13px;
                    }
                }
            }
        }
    }

    .supsis-tag-el {
        padding: 5px 12px;
        border: 1px solid var(--pipeline-color);
        color: var(--pipeline-color);
        border-radius: 4px;
        width: max-content;
        font-weight: 500;
        font-size: 12px;
        background: transparent;
    }

    .agent-detail {
        display: flex;
        align-items: center;
        gap: 10px;
        
        > div:last-child {
            font-size: 13px;
            font-weight: 400;
            color: #495057;
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    .task-id-cell {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        padding: 4px 10px;
        background: #495057;
        border-radius: 4px;
        width: max-content;
        margin: 0 auto;

        .material-icons {
            color: white;
            font-size: 14px;
        }

        .task-id-text {
            color: white;
            font-weight: 600;
            font-size: 12px;
        }
    }

    .date-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #6c757d;
        font-size: 13px;

        .material-icons {
            font-size: 16px;
            color: #adb5bd;
        }
    }

    .workflow-cell {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #495057;
        font-weight: 500;

        .material-icons {
            font-size: 16px;
            color: #6c757d;
        }
    }
}
</style>

